import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
// iter101.90 — Checkbox import removed; per-row selection UI is gone.
import { toast } from "sonner";
import {
    Plus, ClipboardText, Phone, MagnifyingGlass, UserPlus, Storefront, ArrowRight,
    Users, SquaresFour, ListBullets, X, Wallet, DownloadSimple, UploadSimple, Tag, CaretDown,
    Sparkle, CalendarBlank, ChatCircleDots, EnvelopeSimple,
    Bell, CheckCircle, TrendUp, ShieldCheck, CalendarPlus, Stethoscope,
    Warning, NotePencil, User as UserIcon, PaperPlaneTilt,
    // iter98.99dk — Icons for the redesigned KPI strip on the Patients tab.
    ClockClockwise, Hourglass,
} from "@phosphor-icons/react";
import PatientAvatar from "@/components/patient/PatientAvatar";
import AddPatientDialog from "@/components/doctor/AddPatientDialog";
import Sparkline from "@/components/doctor/Sparkline";
import DemoBadge from "@/components/case/DemoBadge";
import { statusLabel as sharedStatusLabel, statusTone as sharedStatusTone } from "@/lib/caseStatus";

/* Case-status → phase filter options. Used by the "Segment by phase" select
   on the My Patients filter strip. `""` = no phase filter; otherwise the
   filter matches the patient's LATEST case status. Order = lifecycle order.
   iter101.0 — Filter labels now source from `/lib/caseStatus.js` so the
   dropdown speaks the same language as the pill next to each patient. */
const PHASE_OPTION_STATUSES = [
    "draft",
    "submitted",
    "assigned_to_planner",
    "in_planning",
    "pending_admin_review",
    "in_qa_review",
    "sent_for_approval",
    "approved",
    "in_production",
    "shipped",
    "in_treatment",
    "treatment_started",
    "refinement_requested",
    "completed",
    "cancelled",
];
const PHASE_OPTIONS = [
    { value: "", label: "All phases" },
    ...PHASE_OPTION_STATUSES.map((s) => ({ value: s, label: sharedStatusLabel(s, "doctor") })),
];

/* Latest-case status pill map — tone class per shared caseStatus tone
   so every doctor surface reads the same colours + labels. */
const TONE_TO_PILL_CLS = {
    slate:   "bg-zinc-100 text-zinc-700",
    sky:     "bg-sky-100 text-sky-800",
    violet:  "bg-violet-100 text-violet-800",
    amber:   "bg-amber-100 text-amber-800",
    rose:    "bg-rose-100 text-rose-700",
    emerald: "bg-emerald-100 text-emerald-800",
};
function statusTheme(status) {
    if (!status) return { label: "No case", bg: "bg-zinc-100 text-zinc-500" };
    const label = sharedStatusLabel(status, "doctor") || status.replace(/_/g, " ");
    const bg = TONE_TO_PILL_CLS[sharedStatusTone(status)] || "bg-zinc-100 text-zinc-700";
    return { label, bg };
}

const initials = (n) => (n || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

/* Persist the chosen view between sessions so a doctor who likes the list mode keeps it. */
const VIEW_KEY = "doctor.patientsView";
const readPersistedView = () => {
    if (typeof window === "undefined") return "list";
    return window.localStorage.getItem(VIEW_KEY) === "grid" ? "grid" : "list";
};

export default function DoctorPatients() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [patients, setPatients] = useState([]);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    // Filter pills: all | active | walkin | nocase | balance | needs_appt | new30.
    // `?balance=open` URL param (from Dashboard "Money owed to you" card)
    // pre-selects the balance filter so doctors land straight on owed patients.
    const initialFilter = searchParams.get("balance") === "open" ? "balance" : "all";
    const [filter, setFilter] = useState(initialFilter);
    const [view, setView] = useState(readPersistedView);
    // iter100.189 — `?add=1` URL param (from Dashboard "New Patient"
    // quick action) auto-opens the Add Patient dialog on mount and
    // strips the param from the URL so a browser back/forward doesn't
    // re-open the dialog uninvited.
    const [creatingPatient, setCreatingPatient] = useState(searchParams.get("add") === "1");
    useEffect(() => {
        if (searchParams.get("add") === "1") {
            const next = new URLSearchParams(searchParams);
            next.delete("add");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [phase, setPhase] = useState("");
    // Sort dropdown — name | lastvisit | balance | progress
    const [sort, setSort] = useState("");
    // iter98.34 — Reverted preview-drawer pattern. Clicking a patient row
    // now navigates directly to the full profile page (the old behaviour
    // before iter97.x); doctors found the centered preview-popup
    // confusing per 2026-06-12 user feedback. The PatientDrawer component
    // is kept in the file (unused) so we can re-enable it later if needed.
    // const [drawer, setDrawer] = useState(null);
    // iter101.90 — Bulk select removed to match Aligner Cases row grammar
    // (no per-row checkbox). BulkTagDialog + bulk-tag endpoint still exist
    // and can be re-wired later via a row-level menu action.
    const [exporting, setExporting] = useState(false);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/doctor/patients"),
            api.get("/cases"),
        ]).then(([p, c]) => {
            setPatients(p.data);
            setCases(c.data);
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* Index cases by patient so we don't quadratically lookup latest case + activity. */
    const caseIndex = useMemo(() => {
        const idx = {};
        cases.forEach((c) => {
            const arr = idx[c.patient_id] || (idx[c.patient_id] = []);
            arr.push(c);
        });
        // Sort each patient's cases by created_at desc so [0] is the latest.
        Object.values(idx).forEach((arr) => arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
        return idx;
    }, [cases]);

    const hasActiveCase = (pid) => (caseIndex[pid] || []).some((c) => !["cancelled", "completed"].includes(c.status));
    const latestCase = (pid) => (caseIndex[pid] || [])[0] || null;
    /* Outstanding patient balance — sum of `payments_summary.balance` across
       all of a patient's cases. Powers the "Money owed" filter pill and the
       inline balance badge on the row.

       iter100.316 — Exclude `draft` cases. A preliminary quote entered
       in the New-Case wizard was inflating "Money owed" numbers even
       though nothing was ever billed to the patient.
       iter100.70 — Also exclude `cancelled` cases. A cancelled case's
       quote is not a receivable; nothing was ever billed. The backend
       `_enrich_case` now zeroes `payments_summary.balance` for both
       statuses, so the `bal <= 0` guard below would already trip — we
       keep the explicit filter for clarity. Aligned with the Dashboard's
       patientOutstanding calculator. */
    /* iter101.94 — "Patient owes" now reads `p.outstanding_balance`
       from the backend, which sums case-level A/R + open-invoice A/R
       (sent/viewed/partial/overdue statuses from `db.invoices`).
       Before this fix the frontend re-derived balance from `cases`
       only, so invoices the doctor issued from the Billing tab
       showed no signal on the Patients-list column. Everything the
       doctor has invoiced (once sent) now flows through. */
    const patientBalance = (pid) => {
        const p = patients.find((x) => x.id === pid);
        return Number(p?.outstanding_balance || 0);
    };

    /* Top-strip stats — iter98.99dk: redesigned to surface more
       clinically/operationally relevant metrics. Removed:
         • Added in 30 days (recency vanity metric)
         • Outstanding (already a filter chip below)
         • Needs appointment (replaced by inverse "Booked this week")

       New focus: doctor's *clinical workload* + *relationship hygiene*
       at a glance.  All KPIs computed from data already on the patient
       object — no extra endpoint calls. */
    const stats = useMemo(() => {
        const total = patients.length;
        let withCase = 0, walkin = 0;
        let inTreatment = 0;        // Aligners issued — currently being worn
        let recordsPending = 0;     // Case open but pre-aligner (draft / planning)
        let bookedThisWeek = 0;     // next_appointment in next 7 days
        let staleRelationship = 0;  // active case + no visit in 30d+
        let owed = 0, owedTotal = 0;          // Kept for the "Money owed" filter chip below.
        let recent = 0;                       // Kept for the "New 30d" filter chip.
        const now = Date.now();
        const SEVEN_DAYS = 7 * 86400000;
        const THIRTY_DAYS = 30 * 86400000;
        const cutoff30Iso = new Date(now - THIRTY_DAYS).toISOString().slice(0, 10);
        const IN_TREATMENT_STATUSES = new Set([
            "in_treatment", "treatment_started", "aligners_received",
            "fitted", "in_production", "shipped",
            "submitted_to_production", "assigned_to_production",
        ]);
        const RECORDS_PENDING_STATUSES = new Set([
            "draft", "submitted", "in_planning",
            "scan_visit_scheduled", "scan_visit_completed",
            "sent_for_approval", "ready_for_review",
        ]);
        patients.forEach((p) => {
            const status = p.latest_case_status;
            const active = hasActiveCase(p.id);
            if (active) withCase += 1;
            if (p.is_walk_in === true) walkin += 1;
            if (status && IN_TREATMENT_STATUSES.has(status)) inTreatment += 1;
            if (status && RECORDS_PENDING_STATUSES.has(status)) recordsPending += 1;

            const nextApptDate = p.next_appointment?.date;
            if (nextApptDate) {
                const dt = new Date(nextApptDate).getTime();
                if (!Number.isNaN(dt) && dt >= now && dt - now <= SEVEN_DAYS) bookedThisWeek += 1;
            }
            if (active) {
                const lvRaw = p.last_visit;
                const lvTs = lvRaw ? new Date(lvRaw).getTime() : null;
                const stale = !lvTs || (now - lvTs) > THIRTY_DAYS;
                if (stale) staleRelationship += 1;
            }
            if ((p.created_at || "").slice(0, 10) >= cutoff30Iso) recent += 1;
            const bal = patientBalance(p.id);
            if (bal > 0) { owed += 1; owedTotal += bal; }
        });
        return {
            total, withCase, walkin,
            inTreatment, recordsPending, bookedThisWeek, staleRelationship,
            owed, owedTotal, recent,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patients, caseIndex]);

    /* Filter + search compose: pills first, then text search, then phase. */
    const filtered = useMemo(() => {
        let list = patients;
        if (filter === "active") list = list.filter((p) => hasActiveCase(p.id));
        else if (filter === "walkin") list = list.filter((p) => p.is_walk_in === true);
        else if (filter === "nocase") list = list.filter((p) => !hasActiveCase(p.id));
        else if (filter === "balance") list = list.filter((p) => patientBalance(p.id) > 0);
        else if (filter === "in_treatment") {
            // iter98.99dk — Click-through from the "In treatment" KPI tile.
            const set = new Set([
                "in_treatment", "treatment_started", "aligners_received",
                "fitted", "in_production", "shipped",
                "submitted_to_production", "assigned_to_production",
            ]);
            list = list.filter((p) => set.has(p.latest_case_status));
        }
        else if (filter === "records_pending") {
            const set = new Set([
                "draft", "submitted", "in_planning",
                "scan_visit_scheduled", "scan_visit_completed",
                "sent_for_approval", "ready_for_review",
            ]);
            list = list.filter((p) => set.has(p.latest_case_status));
        }
        else if (filter === "booked_week") {
            const now = Date.now();
            const SEVEN_DAYS = 7 * 86400000;
            list = list.filter((p) => {
                const d = p.next_appointment?.date;
                if (!d) return false;
                const ts = new Date(d).getTime();
                return !Number.isNaN(ts) && ts >= now && ts - now <= SEVEN_DAYS;
            });
        }
        else if (filter === "stale_30d") {
            const now = Date.now();
            const THIRTY_DAYS = 30 * 86400000;
            list = list.filter((p) => {
                if (!hasActiveCase(p.id)) return false;
                const lv = p.last_visit;
                const ts = lv ? new Date(lv).getTime() : null;
                return !ts || (now - ts) > THIRTY_DAYS;
            });
        }
        else if (filter === "new30") {
            const cutoff = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
            list = list.filter((p) => (p.created_at || "").slice(0, 10) >= cutoff);
        }

        if (phase) {
            list = list.filter((p) => latestCase(p.id)?.status === phase);
        }

        if (q.trim()) {
            const l = q.trim().toLowerCase();
            list = list.filter((p) =>
                (p.name || "").toLowerCase().includes(l) ||
                (p.phone || "").includes(q) ||
                (p.email || "").toLowerCase().includes(l) ||
                (p.doctor_tags || []).some((t) => (t || "").toLowerCase().includes(l)) ||
                (latestCase(p.id)?.code || "").toLowerCase().includes(l)
            );
        }
        // Sort routing — explicit sort option overrides defaults.
        if (sort === "name") list = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        else if (sort === "balance") list = [...list].sort((a, b) => patientBalance(b.id) - patientBalance(a.id));
        else if (sort === "progress") list = [...list].sort((a, b) => ((b.latest_case_progress || 0) - (a.latest_case_progress || 0)));
        else if (sort === "lastvisit") list = [...list].sort((a, b) => (b.last_visit || "").localeCompare(a.last_visit || ""));
        // Money-owed view: highest balance first regardless of sort selection
        // because the filter intent is "who do I chase first".
        else if (filter === "balance") {
            list = [...list].sort((a, b) => patientBalance(b.id) - patientBalance(a.id));
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patients, q, filter, phase, sort, caseIndex]);

    // iter101.90 — Bulk selection state + effect removed. Row checkboxes
    // are gone (matching Aligner Cases grammar). If you re-add bulk
    // actions later, restore `selectedIds` + the reset effect.

    // iter97.87 — submitPatient + form state moved into AddPatientDialog.

    const setViewPersisted = (v) => {
        try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
        setView(v);
    };

    // CSV export — server streams a text/csv response with a generated
    // filename. We fetch as blob so the browser triggers a save dialog.
    const exportCsv = async () => {
        setExporting(true);
        try {
            const res = await api.get("/doctor/patients/export", { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `invisique-patients-${stamp}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Patient list downloaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't export");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="doctor-patients-page" className="space-y-8">
            {/* HEADER — matches /doctor/cases exactly (same scale, same button styles)
                so the two main doctor tabs read as a consistent family. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">My Patients</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Everyone who&apos;s walked through your clinic or booked online with you.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={() => navigate("/doctor/patients/import")}
                        data-testid="doctor-import-patients-btn"
                    >
                        <UploadSimple size={13} className="mr-1" /> Import
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={exportCsv}
                        disabled={exporting || patients.length === 0}
                        data-testid="doctor-export-patients-btn"
                    >
                        <DownloadSimple size={13} className="mr-1" /> {exporting ? "Exporting…" : "Export"}
                    </Button>
                    <Button
                        size="sm"
                        className="rounded-full"
                        onClick={() => setCreatingPatient(true)}
                        data-testid="doctor-add-patient-btn"
                    >
                        <Plus size={14} className="mr-1" /> Add patient
                    </Button>
                </div>
            </div>

            {/* iter98.99dk — Redesigned KPI strip.
                Out: Added in 30 days, Outstanding, Needs appointment.
                In: In treatment, Records pending, Booked this week,
                Stalled (no visit in 30d+). Every tile is now clickable
                and drives the matching filter pill below — the strip
                isn't a vanity summary anymore, it's a one-tap drill-in
                cockpit. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <StatTile
                    label="Total patients" value={stats.total} Icon={Users} tone="primary"
                    hint="On your roster"
                    onClick={() => { setFilter("all"); }}
                    testid="stat-total-patients"
                />
                <StatTile
                    label="In treatment" value={stats.inTreatment} Icon={Stethoscope} tone="primary"
                    hint={stats.total > 0 ? `${Math.round(stats.inTreatment / stats.total * 100)}% of roster` : "No data yet"}
                    onClick={() => { setFilter("in_treatment"); }}
                    testid="stat-in-treatment"
                />
                <StatTile
                    label="Records pending" value={stats.recordsPending} Icon={Hourglass} tone="amber"
                    hint={stats.recordsPending > 0 ? "Pre-aligner stage" : "All clear"}
                    onClick={() => { setFilter("records_pending"); }}
                    testid="stat-records-pending"
                />
                <StatTile
                    label="Booked this week" value={stats.bookedThisWeek} Icon={CalendarPlus} tone="primary"
                    hint={stats.bookedThisWeek > 0 ? "Next 7 days" : "Nothing booked yet"}
                    onClick={() => { setFilter("booked_week"); }}
                    testid="stat-booked-week"
                />
                <StatTile
                    label="No visit 30d+" value={stats.staleRelationship} Icon={ClockClockwise} tone="rose"
                    hint={stats.staleRelationship > 0 ? "Touch base soon" : "All caught up"}
                    onClick={() => { setFilter("stale_30d"); }}
                    testid="stat-stale-30d"
                />
                <StatTile
                    label="Walk-ins" value={stats.walkin} Icon={Storefront} tone="violet"
                    hint="Managed by clinic"
                    onClick={() => { setFilter("walkin"); }}
                    testid="stat-walkins"
                />
            </div>

            {/* FILTER STRIP — row 1: search + sort + phase + view toggle.
                iter100.197 · Search extracted to shared FilterSearchBar
                primitive; sort/phase/view controls live in the right-slot. */}
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search name, phone, email…"
                searchTestid="doctor-patients-search"
                testid="patients-filter-bar"
                rightSlot={
                    <>
                        <div className="relative">
                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value)}
                                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                data-testid="patients-sort-select"
                                aria-label="Sort patients"
                            >
                                <option value="">Sort</option>
                                <option value="name">Name A–Z</option>
                                <option value="lastvisit">Last visit</option>
                                <option value="balance">Patient owes</option>
                                <option value="progress">Progress</option>
                            </select>
                            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                        </div>
                        <div className="relative">
                            <select
                                value={phase}
                                onChange={(e) => setPhase(e.target.value)}
                                className="appearance-none h-9 pl-3 pr-8 rounded-md border border-border bg-card text-[12px] font-medium text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer"
                                data-testid="patients-phase-filter"
                                aria-label="Filter by case phase"
                            >
                                {PHASE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <CaretDown size={10} weight="bold" className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                        </div>
                        <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9">
                            <button
                                type="button" onClick={() => setViewPersisted("list")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="view-toggle-list"
                                aria-pressed={view === "list"}
                                title="List view"
                            >
                                <ListBullets size={13} weight="bold" />
                            </button>
                            <button
                                type="button" onClick={() => setViewPersisted("grid")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="view-toggle-grid"
                                aria-pressed={view === "grid"}
                                title="Grid view"
                            >
                                <SquaresFour size={13} weight="bold" />
                            </button>
                        </div>
                    </>
                }
            />

            {/* FILTER STRIP — row 2: chip row via shared primitive.
                `balance` chip additionally toggles the ?balance=open
                URL param so the filter survives navigation, so we wrap
                the setter here. */}
            <FilterChipRow
                value={filter}
                onChange={(nextId) => {
                    setFilter(nextId);
                    const next = new URLSearchParams(searchParams);
                    if (nextId === "balance") next.set("balance", "open");
                    else next.delete("balance");
                    setSearchParams(next, { replace: true });
                }}
                testid="patients-filter-chips"
                chipTestidPrefix="filter-pill"
                chips={[
                    { id: "all",     label: "All",          count: stats.total,                  tone: "primary" },
                    { id: "active",  label: "Active case",  count: stats.withCase,               tone: "primary" },
                    { id: "walkin",  label: "Walk-ins",     count: stats.walkin,                 tone: "violet"  },
                    { id: "nocase",  label: "No case yet",  count: stats.total - stats.withCase, tone: "slate"   },
                    { id: "balance", label: "Money owed",   count: stats.owed, tone: "rose",    Icon: Wallet   },
                    /* iter98.99dm — "Needs appt" chip is intentionally
                       omitted until the appointment-recommender ships. */
                    { id: "new30",   label: "New (30d)",    count: stats.recent, tone: "slate", Icon: Sparkle  },
                ]}
            />

            {/* iter101.90 — Bulk action bar removed. The row-level
                checkbox that fed it is gone (per user direction:
                "aligner cases rows doesnt have a check box, i dont
                know why do we want that for patients"). Bulk tagging
                can be re-introduced via a menu row-level action later
                if needed. */}

            {/* BODY */}
            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading patients…</div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    total={patients.length}
                    onAdd={() => setCreatingPatient(true)}
                    isFiltered={q.length > 0 || filter !== "all" || phase !== ""}
                    onClear={() => { setQ(""); setFilter("all"); setPhase(""); }}
                />
            ) : view === "list" ? (
                <ListView
                    rows={filtered}
                    latestCase={latestCase}
                    patientBalance={patientBalance}
                    onOpenDrawer={(p) => navigate(`/doctor/patients/${p.id}`)}
                    totalCount={patients.length}
                />
            ) : (
                <GridView rows={filtered} latestCase={latestCase} patientBalance={patientBalance} onOpenDrawer={(p) => navigate(`/doctor/patients/${p.id}`)} />
            )}

            {/* iter98.34 — PatientDrawer preview popup retired per user
                feedback (confusing UX). Row click + "View profile" menu
                item now navigate straight to /doctor/patients/{id}. The
                <PatientDrawer/> component is intentionally NOT mounted
                here; its function definition stays in the file as dead
                code so we can revive it later without re-extracting all
                the timeline/balance/wear logic. */}

            {/* iter97.87 — Add patient dialog now lives in a reusable
                component so it can be re-mounted from the Case Wizard
                (Step 1 inline link). Same behaviour as before. */}
            <AddPatientDialog
                open={creatingPatient}
                onOpenChange={setCreatingPatient}
                onCreated={() => load()}
            />
        </div>
    );
}

/* ---------- Hero stat tile (mockup-matched, no left-rail accent) ---------- */
function StatTile({ label, value, Icon, tone, hint, onClick }) {
    const accent = tone === "primary" ? "bg-primary/10 text-primary"
        : tone === "violet" ? "bg-violet-50 text-violet-700"
        : tone === "amber" ? "bg-amber-50 text-amber-700"
        : tone === "rose" ? "bg-rose-50 text-rose-700"
        : tone === "muted" ? "bg-secondary text-muted-foreground"
        : "bg-primary/10 text-primary";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border border-border p-3 text-left transition ${onClick ? "hover:border-primary/40 hover:shadow-sm cursor-pointer" : ""}`}
            data-testid={`patients-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}

function fmtINRShort(n) {
    const v = Number(n || 0);
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    if (v >= 1000) return `₹${(v / 1000).toFixed(1)}k`;
    return `₹${v.toLocaleString("en-IN")}`;
}
function daysAgo(iso) {
    if (!iso) return null;
    const t = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : "")).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86400000);
}
function formatDateShort(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso + (iso.length === 10 ? "T00:00:00Z" : ""));
        return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
}

/* ---------- LIST VIEW (table — matches Aligner Cases grammar) ----------
   iter101.90 — Row-level checkbox column removed per user direction
   ("aligner cases rows doesnt have a check box, i dont know why do we
   want that for patients. please match the design grammar"). Also
   adopted the CasesPipeline shell: a header strip with a `ListBullets`
   icon + "Patients · N of M" + hint, thead `bg-secondary/60` and
   `px-4` cell padding so both surfaces read as the same family.
*/
function ListView({ rows, latestCase, patientBalance, onOpenDrawer, totalCount }) {
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="patients-list-view">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="inline-flex items-center gap-2 text-sm font-medium">
                    <ListBullets size={15} className="text-primary" weight="duotone" />
                    Patients
                    <span
                        className="text-[10px] text-muted-foreground bg-secondary rounded-full px-2 py-0.5"
                        data-testid="patients-count-chip"
                    >
                        {rows.length}{typeof totalCount === "number" && totalCount !== rows.length ? ` of ${totalCount}` : ""}
                    </span>
                </div>
                <span className="text-[10px] text-muted-foreground">Click a row to open the profile</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                    <thead className="bg-secondary/60">
                        <tr>
                            <Th>Patient (+ Patient ID)</Th>
                            <Th>Latest case</Th>
                            <Th>Compliance</Th>
                            <Th>Last visit</Th>
                            <Th>Next appointment</Th>
                            <Th>Patient owes</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((p) => (
                            <PatientRow
                                key={p.id}
                                p={p}
                                lc={latestCase(p.id)}
                                balance={patientBalance ? patientBalance(p.id) : 0}
                                onOpenDrawer={() => onOpenDrawer(p)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, align = "left", width, className = "" }) {
    return (
        <th
            style={width ? { width } : undefined}
            className={`text-${align} px-4 py-2.5 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border whitespace-nowrap ${className}`}
        >
            {children}
        </th>
    );
}

function PatientRow({ p, lc, balance, onOpenDrawer }) {
    /* iter98.99dj — Removed the per-row 3-dot kebab menu. The same
       actions (view profile / book / message / add case) are reachable
       via the row click + the patient drawer / quick-actions, so the
       extra column added visual noise without unique value.
       iter101.90 — Bulk-select checkbox column removed to match the
       Aligner Cases row grammar (no per-row selection). */
    const navigate = useNavigate();
    const theme = statusTheme(lc?.status);
    const needsDot = balance > 0 || (!p.next_appointment && lc && !["cancelled", "completed"].includes(lc.status));

    return (
        <tr
            onClick={onOpenDrawer}
            className="group cursor-pointer border-b border-border/60 last:border-b-0 transition-colors hover:bg-secondary/40"
            data-testid={`patient-row-${p.id}`}
        >
            <td className="px-4 py-2.5">
                <div className="flex items-center gap-2.5 min-w-0">
                    <PatientAvatar patient={p} size="md" />
                    <div className="min-w-0">
                        <div className="font-medium truncate inline-flex items-center gap-1.5">
                            {p.name || "Unnamed"}
                            {p.is_demo && <DemoBadge />}
                            {needsDot && <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" title="Needs attention" />}
                            {/* iter98.63 — Walk-in badge gated on the
                                explicit `is_walk_in` flag (set at clinic-
                                create), not `source === "clinic"`. The
                                source flips to "clinic" for any patient
                                the case wizard has claimed via the
                                appointment-touchpoint path, which would
                                wrongly tag web-self-booked patients as
                                walk-ins (user-reported, iter98.63). */}
                            {p.is_walk_in === true && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase font-bold tracking-wider inline-flex items-center gap-0.5">
                                    <Storefront size={9} weight="fill" /> Walk-in
                                </span>
                            )}
                            {(p.doctor_tags || []).slice(0, 1).map((t) => (
                                <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary inline-flex items-center gap-0.5">
                                    <Tag size={8} weight="fill" /> {t}
                                </span>
                            ))}
                        </div>
                        <div className="text-[10.5px] text-muted-foreground mt-0.5 font-mono tabular-nums" data-testid={`patient-row-id-${p.id}`}>
                            {/* iter101.88 — Patient ID surfaces under the
                                name so the row echoes the "Patient (+
                                Case ID)" pattern used in Aligner Cases.
                                Uses human_id from the API when present,
                                falls back to a deterministic INV-PT-XXXX
                                derived from the internal id (same rule
                                as CasesPipeline.jsx). */}
                            {p.human_id || `INV-PT-${(p.id || "").replace(/-/g, "").slice(0, 6).toUpperCase()}`}
                        </div>
                    </div>
                </div>
            </td>
            {/* iter101.88 — Contact column removed. The dial + WhatsApp +
                email shortcuts moved into the Patient Profile drawer to
                declutter the row and free horizontal room for the new
                Compliance column. */}
            <td className="px-4 py-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-bold uppercase tracking-wider text-[9px] ${theme.bg}`}>
                    {theme.label}
                </span>
                {lc?.code && (
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5 truncate">{lc.code}</div>
                )}
                {p.latest_case_progress != null && p.latest_case_total_stages > 0 && (
                    <div className="mt-1.5 w-28">
                        <div className="flex items-center justify-between text-[9px] text-muted-foreground tabular-nums">
                            <span>Aligner {p.latest_case_stage}/{p.latest_case_total_stages}</span>
                            <span>{p.latest_case_progress}%</span>
                        </div>
                        <div className="h-[3px] rounded-full bg-secondary mt-0.5 overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${p.latest_case_progress}%` }} />
                        </div>
                    </div>
                )}
            </td>
            {/* iter101.89 — Compliance column. Reads latest_case_compliance
                (patient clinical adherence: "on_time" / "needs_attention"
                / "at_risk" / null for pre-treatment cases). Replaces the
                earlier Invisique-vs-Self mapping (iter101.88) which was
                a misread of the design intent — the column exists to tell
                the doctor "who's actually keeping up with their aligners".
                Sourced from wear-log signals via _compute_patient_compliance
                in patient_utils.py — same math the /doctor/insights/compliance
                dashboard uses, so the two surfaces never disagree. */}
            <td className="px-4 py-2.5" data-testid={`patient-row-compliance-${p.id}`}>
                {p.latest_case_compliance === "on_time" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" title="7-day wear-time on target · logs fresh">
                        <CheckCircle size={10} weight="fill" /> On time
                    </span>
                ) : p.latest_case_compliance === "needs_attention" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 text-amber-800 ring-1 ring-amber-200" title="Wear-time slipping or logs going stale — nudge advised">
                        <Bell size={10} weight="fill" /> Needs attention
                    </span>
                ) : p.latest_case_compliance === "at_risk" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-rose-50 text-rose-800 ring-1 ring-rose-200" title="Low wear-time or patient has gone silent — reach out today">
                        <Warning size={10} weight="fill" /> At risk
                    </span>
                ) : (
                    <span className="text-[10px] text-muted-foreground/60" title="Compliance tracking starts once the patient begins wearing aligners">—</span>
                )}
            </td>
            <td className="px-4 py-2.5">
                {p.last_visit ? (
                    <>
                        <div className="text-[11px] font-medium">{formatDateShort(p.last_visit)}</div>
                        <div className="text-[10px] text-muted-foreground">{daysAgo(p.last_visit)}d ago</div>
                    </>
                ) : <span className="text-[11px] text-muted-foreground italic">No visits</span>}
            </td>
            <td className="px-4 py-2.5">
                {p.next_appointment ? (
                    <>
                        <div className="text-[11px] font-medium">{formatDateShort(p.next_appointment.date)}</div>
                        <div className="text-[10px] text-muted-foreground capitalize truncate max-w-[110px]">{(p.next_appointment.type || "").replace(/_/g, " ")}</div>
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/doctor/appointments?patient_id=${p.id}`); }}
                        className="text-[11px] text-primary font-semibold inline-flex items-center gap-1 hover:underline"
                        data-testid={`patient-row-book-${p.id}`}
                    >
                        <CalendarPlus size={10} /> Book now
                    </button>
                )}
            </td>
            <td className="px-4 py-2.5">
                {balance > 0 ? (
                    <>
                        <div className="text-[12px] font-bold text-rose-700 tabular-nums">₹{Number(balance).toLocaleString("en-IN")}</div>
                        <span className="inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">OVERDUE</span>
                    </>
                ) : (
                    <>
                        <div className="text-[12px] text-muted-foreground">—</div>
                        <div className="text-[10px] text-muted-foreground">Cleared</div>
                    </>
                )}
            </td>
        </tr>
    );
}

/* ---------- GRID VIEW (compact cards, mockup-matched) ---------- */
function GridView({ rows, latestCase, patientBalance, onOpenDrawer }) {
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="patients-grid-view">
            {rows.map((p) => {
                const lc = latestCase(p.id);
                const theme = statusTheme(lc?.status);
                const balance = patientBalance ? patientBalance(p.id) : 0;
                return (
                    <button
                        type="button"
                        key={p.id}
                        onClick={() => onOpenDrawer(p)}
                        className="text-left block bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:shadow-sm transition"
                        data-testid={`patient-card-${p.id}`}
                    >
                        <div className="flex items-start gap-3">
                            <PatientAvatar patient={p} size="lg" />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-display font-semibold truncate">{p.name || "Unnamed"}</div>
                                    {p.is_walk_in === true && (
                                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase font-bold tracking-wider inline-flex items-center gap-0.5">
                                            <Storefront size={9} weight="fill" /> Walk-in
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                                    <Phone size={10} /> {p.phone || "—"}
                                </div>
                            </div>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider text-[9px] shrink-0 ${theme.bg}`}>
                                {theme.label}
                            </span>
                        </div>
                        {p.latest_case_progress != null && p.latest_case_total_stages > 0 && (
                            <div className="mt-3">
                                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                    <span>Aligner {p.latest_case_stage}/{p.latest_case_total_stages}</span>
                                    <span className="font-semibold tabular-nums">{p.latest_case_progress}%</span>
                                </div>
                                <div className="h-[3px] rounded-full bg-secondary mt-1 overflow-hidden">
                                    <div className="h-full bg-primary rounded-full" style={{ width: `${p.latest_case_progress}%` }} />
                                </div>
                            </div>
                        )}
                        <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[11px]">
                            <div>
                                <div className="text-muted-foreground">Next appt</div>
                                <div className="font-medium">
                                    {p.next_appointment ? formatDateShort(p.next_appointment.date) : <span className="text-amber-700">Book now</span>}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Last visit</div>
                                <div className="font-medium">
                                    {p.last_visit ? `${daysAgo(p.last_visit)}d ago` : "—"}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Patient owes</div>
                                <div className={`font-medium tabular-nums ${balance > 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                                    {balance > 0 ? `₹${Number(balance).toLocaleString("en-IN")}` : "Cleared"}
                                </div>
                            </div>
                            <div>
                                <div className="text-muted-foreground">Visits</div>
                                <div className="font-medium tabular-nums">{p.visits ?? 0}</div>
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}


/* ---------- Slide-in PatientDrawer ---------- */
function PatientDrawer({ patient, onClose, latestCase: lc, balance, navigate }) {
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => {
        if (!patient) { setDetail(null); return; }
        setLoadingDetail(true);
        // Fetch wear-logs only if we have an active case to query against —
        // patients without a case won't have any logs to show anyway.
        const requests = [
            api.get(`/doctor/patients/${patient.id}/appointments`),
        ];
        if (lc?.id) {
            requests.push(api.get(`/cases/${lc.id}/wear-logs`));
        }
        Promise.allSettled(requests).then((results) => {
            const appts = results[0].status === "fulfilled" ? results[0].value.data : [];
            const wearLogs = results[1] && results[1].status === "fulfilled" ? results[1].value.data : [];
            setDetail({ appts, wearLogs });
        }).finally(() => setLoadingDetail(false));
    }, [patient, lc?.id]);

    // Build a 7-day wear series (today → 6 days ago) keyed by log_date.
    // Zero-fill missed days so the sparkline conveys "silent" gaps visually.
    // NOTE: useMemo must be called unconditionally, BEFORE any early return,
    // to satisfy the rules-of-hooks linter (and React's invariant).
    const wearWindow = useMemo(() => {
        const logs = detail?.wearLogs || [];
        if (!logs.length) return { series: [], avg: null };
        const byDate = {};
        for (const log of logs) {
            const d = log.log_date;
            if (!d) continue;
            const h = Number(log.hours || 0);
            byDate[d] = (byDate[d] || 0) + h;
        }
        const series = [];
        const today = new Date();
        for (let i = 6; i >= 0; i -= 1) {
            const dt = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
            const iso = dt.toISOString().slice(0, 10);
            series.push(byDate[iso] || 0);
        }
        const logged = series.filter((v) => v > 0);
        const avg = logged.length ? Math.round((logged.reduce((s, v) => s + v, 0) / logged.length) * 10) / 10 : null;
        return { series, avg };
    }, [detail]);

    if (!patient) return null;
    const theme = statusTheme(lc?.status);
    const recentAppts = (detail?.appts || []).slice(0, 5);
    const displayName = (patient.name || "").trim() || "Unnamed patient";
    const hasContact = Boolean(patient.phone || patient.email);

    return (
        <Dialog open={!!patient} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                className="max-w-2xl p-0 gap-0 overflow-hidden"
                data-testid="patient-drawer"
            >
                <DialogHeader className="px-5 py-4 border-b border-border bg-gradient-to-br from-primary/5 to-card">
                    <div className="flex items-center gap-3">
                        <PatientAvatar patient={patient} size="xl" className="ring-2 ring-primary/15" />
                        <div className="flex-1 min-w-0">
                            <DialogTitle className="text-[15px] truncate text-left">{displayName}</DialogTitle>
                            <DialogDescription className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1.5 mt-0.5 text-left">
                                {patient.phone ? <><Phone size={10} /> {patient.phone}</> : <span className="italic">No contact on file</span>}
                                {patient.is_walk_in === true && (
                                    <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase font-bold tracking-wider inline-flex items-center gap-0.5">
                                        <Storefront size={8} weight="fill" /> Walk-in
                                    </span>
                                )}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto p-5 space-y-4">
                    {/* Contact strip — always rendered; disabled state if missing. */}
                    <div className="grid grid-cols-3 gap-2">
                        <ContactChip
                            icon={Phone}
                            label="Call"
                            href={patient.phone ? `tel:${patient.phone.replace(/\s/g, "")}` : null}
                            disabled={!patient.phone}
                            testid="drawer-call-btn"
                        />
                        <ContactChip
                            icon={ChatCircleDots}
                            label="WhatsApp"
                            href={patient.phone ? `https://wa.me/${patient.phone.replace(/\D/g, "")}` : null}
                            external
                            disabled={!patient.phone}
                            accent="emerald"
                            testid="drawer-wa-btn"
                        />
                        <ContactChip
                            icon={EnvelopeSimple}
                            label="Email"
                            href={patient.email ? `mailto:${patient.email}` : null}
                            disabled={!patient.email}
                            testid="drawer-email-btn"
                        />
                    </div>
                    {!hasContact && (
                        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 inline-flex items-center gap-1">
                            <Warning size={10} weight="fill" /> No phone or email on file
                        </div>
                    )}

                    {/* Two-column body — Active case (left) + Appointments (right) on desktop. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <DrawerSection title="Active case" icon={Stethoscope}>
                            {lc ? (
                                <div className="bg-secondary/40 border border-border rounded-lg p-3">
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <span className="text-[12px] font-medium font-mono">{lc.code || "—"}</span>
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] ${theme.bg}`}>
                                            {theme.label}
                                        </span>
                                    </div>
                                    {patient.latest_case_total_stages > 0 ? (
                                        <>
                                            <div className="text-[11px] text-muted-foreground">
                                                Aligner <span className="font-semibold text-foreground tabular-nums">{patient.latest_case_stage}</span> of <span className="tabular-nums">{patient.latest_case_total_stages}</span>
                                            </div>
                                            <div className="h-1.5 rounded-full bg-border mt-2 overflow-hidden">
                                                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${patient.latest_case_progress}%` }} />
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1 tabular-nums">
                                                <span>{patient.latest_case_progress}% complete</span>
                                                <span>{patient.latest_case_progress >= 100 ? "Completed" : "In treatment"}</span>
                                            </div>
                                            {/* 7-day wear sparkline — at-a-glance compliance without leaving the modal. */}
                                            <div className="mt-3 pt-2.5 border-t border-border/60 flex items-center gap-2.5" data-testid="drawer-wear-sparkline">
                                                <div className="shrink-0">
                                                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">7d wear</div>
                                                    <div className={`text-[13px] font-bold tabular-nums leading-none mt-0.5 ${
                                                        wearWindow.avg == null ? "text-muted-foreground"
                                                        : wearWindow.avg >= 20 ? "text-primary"
                                                        : wearWindow.avg >= 16 ? "text-amber-700"
                                                        : "text-rose-700"
                                                    }`}>
                                                        {wearWindow.avg != null ? `${wearWindow.avg}h` : "—"}
                                                    </div>
                                                </div>
                                                <div className={`flex-1 ${
                                                    wearWindow.avg == null ? "text-muted-foreground/60"
                                                    : wearWindow.avg >= 20 ? "text-primary"
                                                    : wearWindow.avg >= 16 ? "text-amber-600"
                                                    : "text-rose-600"
                                                }`}>
                                                    <Sparkline
                                                        data={wearWindow.series}
                                                        width={150}
                                                        height={24}
                                                        targetLine={22}
                                                    />
                                                </div>
                                                <div className="shrink-0 text-[9px] text-muted-foreground text-right leading-tight">
                                                    target<br />22h
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-[11px] text-muted-foreground italic">Aligner stages not yet planned</div>
                                    )}
                                </div>
                            ) : (
                                <DrawerEmpty
                                    icon={Stethoscope}
                                    message="No active case"
                                    ctaLabel="Start a case"
                                    onCta={() => navigate(`/doctor/cases/new?patient_id=${patient.id}`)}
                                />
                            )}
                        </DrawerSection>

                        <DrawerSection title="Appointments" icon={CalendarBlank}>
                            <div className="space-y-1.5">
                                <DrawerMiniStat label="Last visit" value={patient.last_visit ? formatDateShort(patient.last_visit) : "—"} sub={patient.last_visit_type?.replace(/_/g, " ")} />
                                <DrawerMiniStat
                                    label="Next appointment"
                                    value={patient.next_appointment ? formatDateShort(patient.next_appointment.date) : "Not booked"}
                                    sub={patient.next_appointment?.type?.replace(/_/g, " ")}
                                    tone={patient.next_appointment ? "primary" : "amber"}
                                />
                            </div>
                            {!patient.next_appointment && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`/doctor/appointments?patient_id=${patient.id}`)}
                                    className="w-full justify-center rounded-md mt-2 text-xs h-8"
                                    data-testid="drawer-book-btn"
                                >
                                    <CalendarPlus size={12} className="mr-1.5" /> Book appointment
                                </Button>
                            )}
                        </DrawerSection>
                    </div>

                    {/* Balance — full width row */}
                    <DrawerSection title="Balance" icon={Wallet}>
                        <div className={`border rounded-lg p-3 flex items-center justify-between gap-3 ${balance > 0 ? "bg-rose-50/60 border-rose-200" : "bg-secondary/40 border-border"}`}>
                            <div className="min-w-0">
                                <div className={`text-xl font-bold tabular-nums leading-none ${balance > 0 ? "text-rose-700" : "text-primary"}`}>
                                    {balance > 0 ? `₹${Number(balance).toLocaleString("en-IN")}` : "₹0"}
                                </div>
                                <div className="text-[10px] text-muted-foreground mt-1">{balance > 0 ? "Outstanding · overdue" : "All cleared"}</div>
                            </div>
                            {lc && (
                                <div className="flex items-center gap-1.5">
                                    <Button size="sm" variant="outline" onClick={() => navigate(`/doctor/patients/${patient.id}?tab=billing`)} className="text-[11px] rounded-md h-7 px-2.5" data-testid="drawer-record-btn">
                                        <CheckCircle size={11} className="mr-1" /> Record
                                    </Button>
                                    {balance > 0 && (
                                        <Button size="sm" onClick={() => navigate(`/doctor/patients/${patient.id}?tab=billing`)} className="text-[11px] rounded-md h-7 px-2.5" data-testid="drawer-request-btn">
                                            <PaperPlaneTilt size={11} className="mr-1" /> Request
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </DrawerSection>

                    {/* Recent visits */}
                    <DrawerSection title="Recent visits" icon={ClipboardText}>
                        {loadingDetail ? (
                            <div className="text-[11px] text-muted-foreground py-2">Loading…</div>
                        ) : recentAppts.length === 0 ? (
                            <DrawerEmpty icon={ClipboardText} message="No visit history yet" />
                        ) : (
                            <ol className="space-y-2" data-testid="drawer-timeline">
                                {recentAppts.map((a, i) => (
                                    <li key={a.id || i} className="flex items-start gap-2.5 px-1">
                                        <span className={`mt-0.5 w-6 h-6 rounded-full border ${a.status === "completed" ? "bg-primary/10 border-primary/30 text-primary" : "bg-secondary border-border text-muted-foreground"} grid place-items-center shrink-0`}>
                                            <Stethoscope size={11} weight="duotone" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[12px] font-medium capitalize">{(a.appointment_type || a.type || "Visit").replace(/_/g, " ")}</div>
                                            <div className="text-[10px] text-muted-foreground">{formatDateShort(a.date)} {a.time && `· ${a.time}`}</div>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </DrawerSection>

                    {/* COE / Compliance Monitoring — only visible when this patient has VAS active. */}
                    {patient.vas_enabled && (
                        <DrawerSection title="Compliance monitoring" icon={ShieldCheck} badge={
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">
                                <ShieldCheck size={10} weight="fill" /> COE managed
                            </span>
                        }>
                            <div className="bg-secondary/40 border border-border rounded-lg p-3 divide-y divide-border/60">
                                <DrawerRow k="Last COE contact" v={patient.coe_last_contact ? formatDateShort(patient.coe_last_contact) : "—"} />
                                <DrawerRow k="Next scheduled call" v={patient.coe_next_contact ? formatDateShort(patient.coe_next_contact) : "—"} />
                                <DrawerRow k="Wear compliance" v={
                                    patient.coe_wear_status === "on_track"
                                        ? <span className="text-primary font-semibold">✓ On track</span>
                                        : patient.coe_wear_status || "—"
                                } />
                            </div>
                        </DrawerSection>
                    )}
                </div>
                <DialogFooter className="px-5 py-3 border-t border-border bg-card sm:justify-between flex-row gap-1.5">
                    <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" className="text-[11px] h-8 rounded-md px-2.5" data-testid="drawer-add-note">
                            <NotePencil size={12} className="mr-1.5" /> Note
                        </Button>
                        <Button variant="ghost" size="sm" className="text-[11px] h-8 rounded-md px-2.5" onClick={() => navigate(`/doctor/patient-chat?patient=${patient.id}`)} data-testid="drawer-nudge">
                            <Bell size={12} className="mr-1.5" /> Nudge
                        </Button>
                    </div>
                    <Button size="sm" className="text-[11px] h-8 rounded-md px-3" onClick={() => navigate(`/doctor/patients/${patient.id}`)} data-testid="drawer-full-profile">
                        <UserIcon size={12} className="mr-1.5" /> Open profile
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ContactChip({ icon: Icon, label, href, external, disabled, accent, testid }) {
    const className = `inline-flex items-center justify-center gap-1.5 px-2 h-9 rounded-md border text-[11px] font-medium transition ${
        disabled
            ? "bg-secondary/30 border-border text-muted-foreground/50 cursor-not-allowed"
            : accent === "emerald"
                ? "bg-secondary/40 border-border text-foreground hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
                : "bg-secondary/40 border-border text-foreground hover:bg-primary/10 hover:border-primary/40 hover:text-primary"
    }`;
    if (disabled || !href) {
        return (
            <span className={className} data-testid={testid}>
                <Icon size={13} /> {label}
            </span>
        );
    }
    return (
        <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className={className}
            data-testid={testid}
        >
            <Icon size={13} /> {label}
        </a>
    );
}

function DrawerSection({ title, icon: Icon, badge, children }) {
    return (
        <section>
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
                {Icon && <Icon size={11} weight="duotone" className="text-primary" />}
                <span className="flex-1">{title}</span>
                {badge}
            </div>
            {children}
        </section>
    );
}

function DrawerMiniStat({ label, value, sub, tone }) {
    return (
        <div className={`border rounded-lg p-2.5 ${tone === "amber" ? "bg-amber-50/40 border-amber-200" : "bg-secondary/40 border-border"}`}>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
            <div className={`text-[12px] font-semibold mt-0.5 truncate ${tone === "amber" ? "text-amber-700" : "text-foreground"}`}>{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground capitalize truncate">{sub}</div>}
        </div>
    );
}

function DrawerEmpty({ icon: Icon, message, ctaLabel, onCta }) {
    return (
        <div className="bg-secondary/30 border border-dashed border-border rounded-lg px-3 py-4 text-center">
            <Icon size={18} weight="duotone" className="text-muted-foreground mx-auto mb-1.5" />
            <div className="text-[11px] text-muted-foreground">{message}</div>
            {ctaLabel && (
                <button
                    type="button"
                    onClick={onCta}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                >
                    {ctaLabel} <ArrowRight size={10} />
                </button>
            )}
        </div>
    );
}

function DrawerRow({ k, v }) {
    return (
        <div className="flex items-center justify-between gap-2 py-1.5 text-[12px]">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-medium text-right">{v}</span>
        </div>
    );
}

/* ---------- Empty state — adapts to "no filter results" vs "no patients ever" ---------- */
function EmptyState({ total, onAdd, isFiltered, onClear }) {
    if (isFiltered && total > 0) {
        return (
            <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
                <MagnifyingGlass size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No patients match your filters</div>
                <p className="text-xs text-muted-foreground mt-1.5">Try clearing your search or switching to a different filter.</p>
                <Button onClick={onClear} variant="outline" className="rounded-full mt-4" data-testid="empty-clear-filters">
                    Clear filters
                </Button>
            </div>
        );
    }
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center">
            <UserPlus size={28} className="mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-bold">No patients yet</div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                Add walk-in patients here so you can submit aligner cases for them. Online bookings show up automatically.
            </p>
            <Button onClick={onAdd} className="rounded-full mt-5" data-testid="doctor-empty-add-patient">
                <Plus size={14} className="mr-1.5" weight="bold" /> Add your first patient
            </Button>
        </div>
    );
}


/* ---------- Bulk tag dialog ---------- */
function BulkTagDialog({ open, onOpenChange, count, onSubmit }) {
    const [tag, setTag] = useState("");
    const [action, setAction] = useState("add");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setTag("");
            setAction("add");
        }
    }, [open]);

    const submit = async () => {
        if (!tag.trim()) {
            toast.error("Enter a tag");
            return;
        }
        setBusy(true);
        try {
            await onSubmit({ tag: tag.trim(), action });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>
                        {action === "add" ? "Tag" : "Untag"} {count} patient{count === 1 ? "" : "s"}
                    </DialogTitle>
                    <DialogDescription>
                        Tags help you segment patients for follow-up. They&apos;re visible only to your clinic.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    <div className="flex items-center rounded-full border border-border bg-card overflow-hidden text-xs font-semibold">
                        <button
                            type="button"
                            onClick={() => setAction("add")}
                            className={`flex-1 h-9 ${action === "add" ? "bg-primary text-white" : "text-muted-foreground"}`}
                            data-testid="bulk-tag-action-add"
                        >
                            Add tag
                        </button>
                        <button
                            type="button"
                            onClick={() => setAction("remove")}
                            className={`flex-1 h-9 ${action === "remove" ? "bg-rose-600 text-white" : "text-muted-foreground"}`}
                            data-testid="bulk-tag-action-remove"
                        >
                            Remove tag
                        </button>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Tag</Label>
                        <Input
                            value={tag}
                            onChange={(e) => setTag(e.target.value)}
                            placeholder="e.g. follow-up, VIP, refinement-due"
                            className="h-10 mt-1"
                            maxLength={40}
                            data-testid="bulk-tag-input"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Plain text, max 40 characters. Tags appear as chips on each patient row.
                        </p>
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !tag.trim()} data-testid="bulk-tag-submit">
                        {busy ? "Saving…" : action === "add" ? "Add tag" : "Remove tag"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
